import React, { useMemo } from 'react';
import { Experiment } from '@printdeal/feature-management/react';
import { graphql, Link as GatsbyLink, useStaticQuery } from 'gatsby';
import { Link, Container } from '@printdeal/ui-library';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { findNodeByLocale } from '../../../../helpers/translations';
import { getConfiguration } from '../../../../helpers/Configuration';
import KiyohScoreLink from './KiyohScoreLink';
import { dispatchDataLayerUspBarItemClick } from '../helpers';
import TrustPilot from './TrustPilot';

const uspBarQuery = graphql`
  query {
    uspNavigation: allContentfulNavigation(filter: { type: { eq: "USP" } }) {
      nodes {
        title
        node_locale
        items: contentfulchildren {
          ...navItem
        }
      }
    }
  }
`;

type NodeProps = {
  node_locale: string;
  items?: ItemProps[];
  title?: string;
};

type ItemProps = {
  url?: string;
  openInNewTab?: boolean;
  children?: {
    path: string;
  };
  title?: string;
  icon?: string;
};

type UspBarProps = {
  className?: string;
};

type QueryProps = {
  uspNavigation: {
    nodes: NodeProps[];
  }
};

const UspBar = ({ className }: UspBarProps) => {
  const { uspNavigation } = useStaticQuery<QueryProps>(uspBarQuery);
  const configuration = getConfiguration();

  const intl = useIntl();

  const uspNavigationForLocale: NodeProps | undefined = useMemo(() => findNodeByLocale(
    uspNavigation.nodes,
    intl.locale,
  ), [uspNavigation, intl.locale]);

  return (
    <section className={clsx('pd-bg-dim-100 pd-py-5', className)}>
      <Container className="pd-items-center xl:pd-flex" as="nav" aria-label={uspNavigationForLocale?.title}>
        <ul className="pd-flex pd-items-center pd-justify-between pd-gap-4 pd-opacity-60">
          {uspNavigationForLocale?.items?.slice(0, 4).map((item) => {
            const {
              url, openInNewTab, children, title,
            } = item;
            const path = children?.path;

            return (
              <li
                className="pd-flex pd-text-sm pd-leading-tight"
                data-test="usp-bar-item"
                key={`item-${title}`}
              >
                {path && (
                  <Link
                    as={GatsbyLink}
                    variant="secondary"
                    iconBefore="check"
                    to={path}
                    onClick={() => dispatchDataLayerUspBarItemClick(title)}
                    className="pd-gap-1"
                  >
                    {title}
                  </Link>
                )}
                {url && !path && openInNewTab && (
                  <Link
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => dispatchDataLayerUspBarItemClick(title)}
                    className="pd-gap-1"
                  >
                    {title}
                  </Link>
                )}
                {url && !path && !openInNewTab && (
                  <Link
                    href={url}
                    onClick={() => dispatchDataLayerUspBarItemClick(title)}
                    className="pd-gap-1"
                  >
                    {title}
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
        <div className="pd-ml-auto pd-hidden xl:pd-inline-flex">
          <Experiment id="search4it_exp1728_-_kiyoh_reviews_vs_trustpilot_reviews" fallback>
            <Experiment.Control>
              {configuration.kiyoh.enabled && (
                <KiyohScoreLink />
              )}
            </Experiment.Control>
            <Experiment.Variant>
              <TrustPilot />
            </Experiment.Variant>
          </Experiment>
        </div>

      </Container>
    </section>
  );
};

export default UspBar;
